import { useEffect, useState } from "react";
import Header from "../components/Header";
import { useContextData } from "../hooks/useContextData";
import { Box, LinearProgress, Paper, Typography } from "@mui/material";
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useUserData } from "../hooks/useUserData";
import AfterPurchase from "../components/AfterPurchase";
import PriceCards from "../components/PriceCard";

const prices = [
  { searches: 3, price: 7 }, // $2.33 per search
  { searches: 8, price: 14 }, // $1.75 per search
  { searches: 12, price: 17 }, // $1.42 per search
  { searches: 20, price: 19 }, // $0.95 per search - best value!
];

const Pricing = () => {
  const { theme, userInfo } = useContextData();
  const [{ isPending }] = usePayPalScriptReducer();
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [purchasedModalVisible, setPurchasedModalVisible] = useState(false);
  const { fetchUserInfo } = useUserData();

  const waitAndFetchUserInfo = async () => {
    await new Promise((resolve) => setTimeout(resolve, 2400));
    await fetchUserInfo();
  };

  const onApprove = async () => {
    console.log("Payment approved");
    setPurchasedModalVisible(true);
    setSelectedPrice(null);
    const previousTokens = userInfo.tokens;
    await waitAndFetchUserInfo();
    const MAX_RETRIES = 3;
    for (let i = 0; i < MAX_RETRIES; i++) {
      console.log("retry number: ", i);
      await waitAndFetchUserInfo();
      if (previousTokens !== userInfo.tokens) {
        console.log("User info updated successfully");
        break;
      }
    }
  };

  useEffect(() => {
    if (!userInfo.id) {
      fetchUserInfo();
    }
  }, []);

  // console.log("selectedPrice: ", selectedPrice);
  // console.log("userinfo: ", userInfo);

  return (
    <div
      style={{
        backgroundColor: theme === "dark" ? "#00050F" : "white",
        color: theme === "dark" ? "#FAFAFA" : undefined,
        height: "100%",
        minHeight: "100vh",
      }}
    >
      <Header />
      {isPending && <LinearProgress variant="determinate" />}
      <AfterPurchase
        open={purchasedModalVisible}
        setOpen={setPurchasedModalVisible}
      />
      <Typography variant="h4" style={{ marginTop: 15, marginBottom: 15 }}>
        Reverse search made easy
      </Typography>
      <Paper
        elevation={5}
        sx={{
          display: "flex",
          flexDirection: "column",
          marginTop: 3,
          border: 0.5,
          borderColor: theme === "dark" ? "#FAFAFA" : "#15121f",
          borderRadius: 4,
          width: "fit-content",
          padding: 4,
          marginLeft: "auto",
          marginRight: "auto",
          marginBottom: 5,
          backgroundColor: theme === "dark" ? "#00050F" : "white",
          color: theme === "dark" ? "#FAFAFA" : undefined,
        }}
      >
        <PriceCards
          selectedPrice={selectedPrice}
          setSelectedPrice={setSelectedPrice}
        />
        <Typography variant="caption" style={{ marginTop: 15 }}>
          Prices are in USD
        </Typography>
        <Typography
          variant="caption"
          style={{ display: "block", marginTop: 15 }}
        >
          Tokens do <b>not</b> expire. Use them whenever you want.
        </Typography>
      </Paper>
      <Box
        sx={{
          justifyContent: "center",
          alignSelf: "center",
          width: 369,
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {selectedPrice !== null && (
          <>
            <Typography
              variant="h6"
              align="center"
              sx={{ mb: 0, fontWeight: "bold", mt: -3 }}
            >
              Pay ${selectedPrice.toFixed(2)}
            </Typography>
            <ArrowDownwardIcon />
          </>
        )}
        <PayPalButtons
          createOrder={(data, actions) => {
            if (selectedPrice === null) {
              return Promise.reject(new Error("Please select a price option"));
            }

            if (!actions.order) {
              return Promise.reject(new Error("PayPal SDK error"));
            }

            return actions.order.create({
              intent: "CAPTURE",
              application_context: {
                shipping_preference: "NO_SHIPPING",
              },
              purchase_units: [
                {
                  amount: {
                    value: selectedPrice.toString(),
                    currency_code: "USD",
                  },
                  // reference_id: "probando", // Add the user ID as reference_id
                  custom_id: userInfo?.id,
                  description: `Purchase of ${prices.find((p) => p.price === selectedPrice)?.searches} search tokens`,
                },
              ],
            });
          }}
          onApprove={async (data, actions) => {
            // console.log("Payment approved", data);
            if (!actions.order) {
              console.error("PayPal SDK error: actions.order is undefined");
              return Promise.reject(new Error("PayPal SDK error"));
            }

            // First capture the payment to ensure it's completed
            try {
              const details = await actions.order.capture();
              // console.log("Payment completed", details);

              // After payment is confirmed, handle the UI updates and token refresh
              setPurchasedModalVisible(true);
              setSelectedPrice(null);

              // Wait longer for backend to process the payment
              // console.log("Waiting for backend to process payment...");
              await new Promise((resolve) => setTimeout(resolve, 3000));

              // Try multiple times with increasing delays to fetch updated user info
              const previousTokens = userInfo.tokens;
              const MAX_RETRIES = 3;

              for (let i = 0; i < MAX_RETRIES; i++) {
                // console.log(
                //   `Fetching user data, attempt ${i + 1}/${MAX_RETRIES}`,
                // );
                await fetchUserInfo();

                // Check if tokens have been updated
                if (userInfo.tokens !== previousTokens) {
                  // console.log(
                  //   "User tokens updated successfully:",
                  //   userInfo.tokens,
                  // );
                  break;
                }

                // Exponential backoff: wait longer with each retry
                const waitTime = 2000 * Math.pow(1.5, i);
                // console.log(
                //   `No token update yet, waiting ${waitTime}ms before next attempt...`,
                // );
                await new Promise((resolve) => setTimeout(resolve, waitTime));
              }

              // Don't return the details object - void is expected
            } catch (error) {
              console.error("Payment failed:", error);
              return Promise.reject(error);
            }
          }}
        />
      </Box>
    </div>
  );
};

export default Pricing;
